import Image from "next/legacy/image"
import { useRef, useState, useEffect, useMemo } from "react"
import { useRouter } from "next/router"
import { FormProvider, useForm } from "react-hook-form"
import { usePropsObject } from "@/lib/contexts/appContext"
import { useArchiveContext } from "@/lib/contexts/archiveContext-2"
import useBreakpoint from "@/lib/hooks/useBreakpoint"
import useStrings from "@/lib/hooks/useStrings"
import { SearchControl } from "@/common/form/search-control/search-control"
import { FilterControl } from "@/common/form/filter-control/filter-control"
import { SelectControl } from "@/common/form/select-control/select-control"
import { trackSearch } from "@/lib/helpers/tagManager"
import { searchContent } from "@/lib/api/search"
import Close from "@/common/icons/close"
import Block from "@/components/wrappers/block"
import Breadcrumbs from "@/components/base/breadcrumbs/breadcrumbs"
import styles from "./header.module.css"
import classNames from "classnames"

export default function Header({ block }) {
  const {
    tag,
    heading,
    text,
    background,
    image,
    search,
    placeholder,
    subheading,
    resultSingular,
    resultPlural
  } = block.attrs
  const router = useRouter()
  const store = useArchiveContext()
  const object = usePropsObject()
  const formRef = useRef()
  const currentPage = store.getPage()
  const [firstRequest, setFirstRequest] = useState(true)

  const defaultValues = useMemo(() => {
    const values = { search: "" }

    search?.settings.forEach((setting) => {
      if (setting?.name && setting?.options?.length > 0) {
        const defaultOption = setting.options.find(
          (option) => option.default === true
        )
        values[setting.name] = defaultOption
          ? defaultOption.value
          : setting.options[0].value
      }
    })

    return values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const taxonomies = useMemo(() => {
    const taxonomies = []
    search?.filters.forEach((filter) => {
      const key = filter.taxonomy
      defaultValues[key] = filter.type === "filter-dropdown" ? [] : ""
      taxonomies.push(key)
    })

    return taxonomies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const methods = useForm({
    defaultValues,
    mode: "onChange"
  })

  const { watch, getValues, setValue, resetField } = methods
  const formValues = watch(taxonomies)

  // Incoming first request
  useEffect(() => {
    setFirstRequest(false)
    store.resetContext(object?.archive)

    const searchParams = new URLSearchParams(window.location.search)

    if (searchParams.size === 0) {
      store.setLoading(false)
      return
    }

    // we have searchParams – let's update the form
    const data = getValues()
    const phrase = searchParams.get("s") ?? ""
    const page = parseInt(searchParams.get("page") ?? 1, 10)

    store.setPage(page)

    if (phrase && phrase.length > 0) {
      setValue("search", phrase)
    }

    const filters = search?.filters
      .filter((filter) => filter.type === "filter-dropdown")
      .map((filter) => filter.taxonomy)
    const selects = search?.filters
      .filter((filter) => filter.type === "select")
      .map((filter) => filter.taxonomy)
    const settings = search?.settings.map((setting) => setting.name)

    Object.keys(data).forEach((key) => {
      const value = searchParams.get(key)

      if (value === null) {
        return
      }

      // Fill filters
      if (filters.includes(key)) {
        setValue(key, value.split(" "))
      }

      // Fill selects
      if (selects.includes(key)) {
        setValue(key, value)
      }

      // Fill settings
      if (settings.includes(key)) {
        setValue(key, value)
      }
    })

    // then do a search
    doSearch(page)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search)
    const page = parseInt(searchParams.get("page") ?? 1, 10)

    if (store.getLoading()) {
      return
    }

    if (search?.context !== store.getContext()) {
      return
    }

    if (page !== currentPage) {
      doSearch(currentPage)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage])

  useEffect(() => {
    if (firstRequest) {
      return
    }

    store.resetContext(object?.archive)
    store.setLoading(false)

    Object.keys(defaultValues).forEach((key) => {
      resetField(key)
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const doSearch = async (page) => {
    store.setLoading(true)
    formRef.current?.scrollIntoView({ behavior: "smooth" })

    const currentPage = page ?? store.getPage()
    const data = getValues()
    const filters = getFilters(currentPage)
    const view = data?.view ?? "grid"

    await searchContent(data?.search, search?.context, filters, view).then(
      (response) => {
        store.setView(view)
        store.setPage(currentPage)
        store.setList(
          search?.context,
          response.result,
          response.totalCount,
          response.maxPage,
          response.cardType,
          response.hash,
          response.attrs
        )

        pushQuery(window.location.pathname, currentPage)
        trackSearch(data.search, search.context)
        store.setLoading(false)
      }
    )
  }

  const getFilters = (page) => {
    const data = getValues()

    const taxonomy = search?.filters.map((filter) => {
      const name = filter?.taxonomy
      return { taxonomy: name, terms: data[name] }
    })

    const view = data.view ?? "grid"

    return {
      count: view === "grid" ? search?.count || 16 : -1,
      page: page,
      postTypes: search?.postTypes ?? [],
      sortBy: data.sort ?? "",
      taxonomy: taxonomy,
      section: search?.section ?? null,
      locale: router?.locale ?? "sv"
    }
  }

  const pushQuery = (url, page, shallow = true) => {
    const query = {}
    const data = getValues()

    if (page > 1) {
      query.page = page + ""
    }

    Object.keys(data).forEach((key) => {
      if (key === "search") {
        if (data[key].length > 0) {
          query.s = data[key]
        }
        return
      }

      if (defaultValues[key] === data[key]) {
        return
      }

      if (Array.isArray(data[key]) && data[key].length > 0) {
        query[key] = data[key].join(" ")
        return
      }

      if (typeof data[key] === "string") {
        query[key] = data[key]
      }
    })

    router.push(
      {
        pathname: url,
        query: query
      },
      null,
      { shallow: shallow }
    )

    return query
  }

  return (
    <Block
      block={block}
      width="bleed"
      gap="none"
      className={classNames(
        styles.wrapper,
        classNames({
          "has-image": background === "image" && image,
          "is-summer-green": background === "summer-green"
        })
      )}>
      <header>
        <Breadcrumbs
          className="breadcrumbs"
          gap="none"
          width="full"
          type="large"
        />
        <div className="content">
          {tag && <span>{tag}</span>}
          <h1>{heading}</h1>
          {text && <p>{text}</p>}
        </div>
        {image && (
          <figure>
            <Image
              src={image.url}
              alt={image.alt}
              layout="fill"
              objectFit="cover"
            />
          </figure>
        )}
      </header>
      <FormProvider {...methods}>
        <SearchForm
          {...{
            search,
            taxonomies,
            resultSingular,
            resultPlural,
            placeholder,
            subheading,
            formValues,
            methods,
            formRef,
            doSearch
          }}
        />
      </FormProvider>
    </Block>
  )
}

const SearchForm = ({
  search,
  taxonomies,
  resultSingular,
  resultPlural,
  placeholder,
  subheading,
  formValues,
  formRef,
  doSearch,
  methods
}) => {
  const { handleSubmit, control, resetField, watch, getValues, setValue } =
    methods

  const { breakpoint, isMounted } = useBreakpoint()
  const [choices, setChoices] = useState([])
  const [openFilter, setOpenFilter] = useState("")
  const [heading, setHeading] = useState(subheading)
  const store = useArchiveContext()
  const list = store.getList()
  const searchAttrs = store.getAttrs()

  const showButtonLoading =
    ["xsm", "sm"].includes(breakpoint) && isMounted && store.getLoading()

  const hasFiltered = () => {
    const searchParams = new URLSearchParams(window.location.search)

    if (searchParams.get("s")) {
      return true
    }

    return taxonomies.some((taxonomy) => searchParams.has(taxonomy))
  }

  useEffect(() => {
    const totalCount = store.getTotalCount()
    if (search?.isEmpty && totalCount === 0) {
      setHeading(null)
      return
    }

    const afterSearch =
      totalCount === 1 ? resultSingular ?? "" : resultPlural ?? ""
    const text = hasFiltered() ? afterSearch : subheading
    setHeading(text.replace("{{count}}", totalCount))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list])

  useEffect(() => {
    const choices = []

    const hasChoices = search.filters.some(
      (filter) => filter.type === "filter-dropdown"
    )

    if (!hasChoices) {
      return
    }

    taxonomies.forEach((taxonomy) => {
      const values = getValues(taxonomy)

      if (values.length === 0) {
        return
      }

      values.forEach((value) => {
        const option = search.filters
          .find((filter) => filter.taxonomy === taxonomy)
          ?.options.find(
            (option) => option.value.toString() === value.toString()
          )

        if (option) {
          choices.push({
            name: taxonomy,
            value: option.value,
            label: option.label
          })
        }
      })
    })

    setChoices(choices)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues])

  const onOpenFilters = (slug) => {
    if (slug === openFilter) {
      setOpenFilter("")
      return
    }

    setOpenFilter(slug)
  }

  const onClear = (taxonomy, name) => {
    const values = getValues(taxonomy)
    const newValues = values.filter(
      (value) => value.toString() !== name.toString()
    )
    setValue(taxonomy, newValues)

    doSearch(1)
  }

  const onClearAll = () => {
    taxonomies.forEach((taxonomy) => {
      resetField(taxonomy)
    })

    doSearch(1)
  }

  const onFilter = (name, value) => {
    const children =
      search.filters
        .find((filter) => filter.taxonomy === name && filter?.hierarchical)
        ?.options.filter(
          (option) => option.parent === value && option.parent > 0
        ) ?? []

    let current = new Set(getValues(name))
    if (!current.has(value)) {
      current.add(value)
      children.forEach((child) => {
        current.add(child.value)
      })
    } else {
      current.delete(value)
      children.forEach((child) => {
        current.delete(child.value)
      })
    }
    current = [...current]
    setValue(name, current)

    doSearch(1)
  }

  const onSelect = (name, value) => {
    // Check for full reset
    const hasReset = !!search?.filters.find(
      (setting) => setting.taxonomy === name && setting.reset
    )

    if (hasReset) {
      taxonomies.forEach((taxonomy) => {
        resetField(taxonomy)
      })
    }

    setValue(name, value)
    setOpenFilter("")
    doSearch(1)
  }

  const onSettingsChange = (name, value) => {
    setValue(name, value)

    if (name === "view") {
      store.setView(value)
    }

    setOpenFilter("")
    doSearch(1)
  }

  const onResetCallback = (name) => {
    resetField(name)
    doSearch(1)
  }

  const onSubmit = (_, e) => {
    e.preventDefault()
    doSearch(1)
  }

  return (
    <div ref={formRef} className="form-wrapper">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={classNames(
            "search-wrapper",
            "context-" + store.getContext(),
            search?.panelClass
          )}>
          <SearchControl
            {...{ control }}
            isLoading={showButtonLoading}
            name="search"
            className="search"
            onReset={onResetCallback}
            placeholder={placeholder ?? "Sök..."}
            onSubmit={(e) => onSubmit(null, e)}
          />
          {search?.filters.map((filter, index) => {
            const { label, options, strings } = filter
            const parent = filter?.parent ?? false
            const name = filter?.taxonomy
            switch (filter?.type) {
              case "filter-dropdown":
                return (
                  <FilterControl
                    className={classNames("filter", name)}
                    key={name + index}
                    isOpen={openFilter === name}
                    {...{
                      control,
                      label,
                      name: name,
                      hierarchical: filter?.hierarchical,
                      options,
                      strings
                    }}
                    showCount={true}
                    hideEmpty={true}
                    termCounts={searchAttrs?.termCounts[name] ?? []}
                    onReset={onResetCallback}
                    onSelect={(name, value) => onFilter(name, value)}
                    selected={watch(name)}
                    setOpen={onOpenFilters}
                  />
                )
              case "select":
                return (
                  <SelectControl
                    key={name}
                    className={name}
                    isOpen={openFilter === name}
                    {...{
                      control,
                      label,
                      name,
                      parent: parent ? watch(parent) : null
                    }}
                    choices={options}
                    onSelect={(name, value) => onSelect(name, value)}
                    selected={watch(name)}
                    setOpen={onOpenFilters}
                  />
                )
              default:
                return null
            }
          })}
          <Choices {...{ choices, onClear, onClearAll }} />
        </div>
        <div
          className={classNames(
            "sub-wrapper",
            "context-" + store.getContext()
          )}>
          <h2>{store.loading ? null : heading}</h2>
          {search?.settings && (
            <div
              className={classNames(
                "settings-wrapper",
                "settings-" + search?.settings?.length,
                {
                  "is-map-view": store.getView() === "map"
                }
              )}>
              {search?.settings.map((filter) => {
                const { name, label, options } = filter
                return (
                  <SelectControl
                    key={name}
                    className={name}
                    isOpen={openFilter === name}
                    {...{
                      control,
                      label,
                      name: name
                    }}
                    choices={options}
                    onSelect={(name, value) => onSettingsChange(name, value)}
                    selected={watch(name)}
                    setOpen={onOpenFilters}
                  />
                )
              })}
            </div>
          )}
        </div>
      </form>
    </div>
  )
}

const Choices = ({ choices, onClear, onClearAll }) => {
  const { madeChoices, clearAll } = useStrings()

  if (choices.length === 0) {
    return null
  }

  return (
    <div className="choices">
      <b>{madeChoices}:</b>
      <ul>
        {choices.map((choice, index) => {
          return (
            <li key={index} onClick={() => onClear(choice.name, choice.value)}>
              {choice.label}
              <Close />
            </li>
          )
        })}
        <li className="clear-all" onClick={onClearAll}>
          {clearAll}
          <Close />
        </li>
      </ul>
    </div>
  )
}
